import React from "react";
import { useNavigate } from "react-router-dom";
import useAdminNotifications from "../../hooks/useAdminNotifications";
import {
  formatTimeAgo,
  getNotificationIcon,
  getNotificationNavigationLink,
  isNotificationUnread,
  markAllNotificationsRead,
  markNotificationRead,
} from "../utils/adminNotifications";

export default function AdminNotifications() {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading } = useAdminNotifications();

  const handleNotificationClick = async (notification) => {
    if (isNotificationUnread(notification)) {
      try {
        await markNotificationRead(notification.id);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    const link = getNotificationNavigationLink(notification);
    if (link) {
      navigate(link);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  if (loading) {
    return (
      <div className="app-content">
        <div className="container-fluid">
          <p>Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-content">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-1">Notifications</h4>
            {unreadCount > 0 && (
              <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                {unreadCount} unread
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleMarkAllRead}
            >
              Mark all as read
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i
                className="material-icons d-block mb-2"
                style={{ fontSize: "48px", color: "#c5cad3" }}
              >
                notifications_none
              </i>
              <p className="mb-1 fw-semibold">No notifications yet</p>
              <span className="text-muted" style={{ fontSize: "14px" }}>
                We&apos;ll let you know when something happens.
              </span>
            </div>
          </div>
        ) : (
          <div className="card border-0 shadow-sm overflow-hidden">
            <div className="list-group list-group-flush">
              {notifications.map((notification) => {
                const unread = isNotificationUnread(notification);

                return (
                  <button
                    key={notification.id}
                    type="button"
                    className={`list-group-item list-group-item-action text-start notifications-page-item${
                      unread ? " notifications-page-item--unread" : ""
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="d-flex align-items-start gap-3">
                      <div className="notifications-page-item-icon">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-grow-1 min-width-0">
                        <div className="d-flex justify-content-between align-items-start gap-3 mb-1">
                          <strong
                            className="notifications-page-item-title"
                            style={{ fontSize: "14px" }}
                          >
                            {notification.title}
                          </strong>
                          <small className="notifications-page-item-time text-nowrap">
                            {formatTimeAgo(notification.createdAt)}
                          </small>
                        </div>
                        <p className="mb-0 notifications-page-item-message">
                          {notification.message}
                        </p>
                      </div>
                      {unread && <span className="notifications-page-unread-dot" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
